/**
 * Helper functions for My Profile page
 * Transforms UserProfileReportData to display format
 */

import type { UserProfileTopStrength, UserProfileReportData } from 'pure-js/types/UserProfileReport'
import type { PersonalityScores } from 'pure-js/types/Personality'
import type { User } from 'pure-js/types/User'
import { FeedbackStatusLevel } from 'pure-js/enums/FeedbackLevel'
import { TFunction } from 'i18next'
import { INDIVIDUAL_PROFILE_LABELS } from '../../../feedbackGenerator/utils/individualProfileMapping'
import { generateIndividualProfileDescription } from '../../../feedbackGenerator/utils/profileDescriptionGenerator'
import type { ProfileIndicator } from '../../../feedbackGenerator/components/profileIndicatorTypes'
import { getVariableLabel as getVariableLabelShared } from '../../../../shared/utils/variableTranslationHelpers'
import { resolveStrengthVariableForLocales } from './resolveStrengthVariableForLocales'

/**
 * MyProfileSectionData - Simplified data structure for MyProfileSubSectionCard
 */
export type MyProfileSectionData = Record<string, { text: string }>

const analysisToPlainText = (value?: unknown): string => {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()

  const blocks = (value as any)?.blocks
  if (!Array.isArray(blocks) || blocks.length === 0) return ''

  return blocks
    .map((b: any) => (typeof b?.text === 'string' ? b.text : ''))
    .join('\n')
    .trim()
}

/**
 * Personality trait pair for visualization
 */
export interface PersonalityTraitPair {
  left: string
  right: string
  value: number
  id: string
}

/**
 * Transform top strengths to profile name (e.g., "Disciplined Moderator")
 */
export function transformTopStrengthsToProfileName(topStrengths: UserProfileTopStrength[], t: TFunction): string {
  if (!topStrengths || topStrengths.length === 0) {
    return t('gamer.collaborationProfile.profileSummary.title', 'Your Profile')
  }

  if (topStrengths.length === 1) {
    const label = INDIVIDUAL_PROFILE_LABELS[topStrengths[0].variable]?.topStrength || 'Strong'
    return label
  }

  // Use first two strengths to generate combined title
  const topLabel = INDIVIDUAL_PROFILE_LABELS[topStrengths[0].variable]?.topStrength || 'Strong'
  const secondLabel = INDIVIDUAL_PROFILE_LABELS[topStrengths[1].variable]?.secondStrength || 'Performer'

  return `${topLabel} ${secondLabel}`
}

/** The words shown for each status; a good strength reads as outstanding. */
const STATUS_LABELS: Record<FeedbackStatusLevel, { key: string; defaultValue: string }> = {
  [FeedbackStatusLevel.EXCELLENT]: { key: 'gamer.feedback.status.excellent', defaultValue: 'Excellent' },
  [FeedbackStatusLevel.GOOD]: { key: 'gamer.feedback.status.outstanding', defaultValue: 'Outstanding' },
  [FeedbackStatusLevel.NEEDS_WORK]: { key: 'gamer.feedback.status.needsWork', defaultValue: 'Needs Work' }
}

/** A strength the answer gives no status, or one the app has no words for, reads as good, the middle one. */
const STATUS_LEVEL_WHEN_MISSING = FeedbackStatusLevel.GOOD

const FEEDBACK_STATUS_LEVELS = Object.values(FeedbackStatusLevel)

export const readFeedbackStatusLevel = (statusLevel?: string): FeedbackStatusLevel =>
  FEEDBACK_STATUS_LEVELS.find((level) => level === statusLevel) ?? STATUS_LEVEL_WHEN_MISSING

/**
 * Map a strength's status level to its status text
 */
export function mapLevelToStatus(
  statusLevel: FeedbackStatusLevel | undefined,
  t: TFunction
): { status: string; level: FeedbackStatusLevel } {
  const level = readFeedbackStatusLevel(statusLevel)
  const label = STATUS_LABELS[level]

  return { status: t(label.key, { defaultValue: label.defaultValue }), level }
}

/**
 * Transform top strengths to ProfileIndicator format for ProfileCardWithIndicators
 */
export function transformTopStrengthsToIndicators(
  topStrengths: UserProfileTopStrength[],
  t: TFunction
): ProfileIndicator[] {
  if (!topStrengths || topStrengths.length === 0) {
    return []
  }

  // Take first 2 strengths and transform to indicators
  return topStrengths.slice(0, 2).map((strength) => {
    const { status, level } = mapLevelToStatus(strength.statusLevel, t)

    // Use shared getVariableLabel function with BO translation support
    const resolvedVariable = resolveStrengthVariableForLocales(strength.variable)
    const label = getVariableLabelShared(resolvedVariable, t, { context: 'strengths', type: 'label' })
    const tooltipResolved = getVariableLabelShared(resolvedVariable, t, { context: 'strengths', type: 'tooltip' })
    const trimmedLabel = label.trim()
    const trimmedTooltip = tooltipResolved.trim()
    const finalTooltip =
      trimmedTooltip !== '' && trimmedTooltip !== trimmedLabel
        ? trimmedTooltip
        : trimmedLabel !== ''
          ? trimmedLabel
          : undefined

    return {
      label,
      status,
      level,
      tooltip: finalTooltip
    }
  })
}

/**
 * Generate profile description from top strengths
 */
export function generateProfileDescription(topStrengths: UserProfileTopStrength[], t: TFunction): string {
  if (!topStrengths || topStrengths.length < 2) {
    return t('gamer.collaborationProfile.profileSummary.title', 'Your top strengths')
  }

  // UserProfileTopStrength is compatible with StrengthProfile (both have variable and normalizedScore)
  return generateIndividualProfileDescription(
    topStrengths[0] as any, // Type assertion - UserProfileTopStrength is compatible with StrengthProfile
    topStrengths[1] as any,
    t
  )
}

/**
 * Transform personality scores to trait pairs for visualization
 * Maps PersonalityScores to slider pairs with values (0-100)
 */
export function transformPersonalityScoresToTraits(scores: PersonalityScores, t: TFunction): PersonalityTraitPair[] {
  const traits: PersonalityTraitPair[] = []

  // Map each personality dimension to a trait pair
  // Score is 0-1, convert to 0-100 for visualization
  // Higher score means more of the "right" trait, lower score means more of the "left" trait
  // We calculate position as percentage (0-100) where 50 is neutral

  // Order: conscientiousness, openness, neuroticism, extroversion, agreeableness, communication, helpingAssisting, decisionMaking

  // Conscientiousness: Adaptable (left) vs Structured (right)
  if (scores.conscientiousness !== undefined) {
    const value = Math.round(scores.conscientiousness * 100)
    traits.push({
      id: 'conscientiousness',
      left: t('gamer.collaborationProfile.personalityTraits.adaptable', { defaultValue: 'Adaptable' }),
      right: t('gamer.collaborationProfile.personalityTraits.structured', { defaultValue: 'Structured' }),
      value
    })
  }

  // Openness: Practical (left) vs Experimental (right)
  if (scores.openness !== undefined) {
    const value = Math.round(scores.openness * 100)
    traits.push({
      id: 'openness',
      left: t('gamer.collaborationProfile.personalityTraits.practical', { defaultValue: 'Practical' }),
      right: t('gamer.collaborationProfile.personalityTraits.experimental', { defaultValue: 'Experimental' }),
      value
    })
  }

  // Neuroticism: Stable (left) vs Passionate (right)
  if (scores.neuroticism !== undefined) {
    const value = Math.round(scores.neuroticism * 100)
    traits.push({
      id: 'neuroticism',
      left: t('gamer.collaborationProfile.personalityTraits.stable', { defaultValue: 'Stable' }),
      right: t('gamer.collaborationProfile.personalityTraits.passionate', { defaultValue: 'Passionate' }),
      value
    })
  }

  // Extroversion: Focus (left) vs Energetic (right)
  if (scores.extroversion !== undefined) {
    const value = Math.round(scores.extroversion * 100)
    traits.push({
      id: 'extroversion',
      left: t('gamer.collaborationProfile.personalityTraits.focus', { defaultValue: 'Focus' }),
      right: t('gamer.collaborationProfile.personalityTraits.energetic', { defaultValue: 'Energetic' }),
      value
    })
  }

  // Agreeableness: Straightforward (left) vs Empathic (right)
  if (scores.agreeableness !== undefined) {
    const value = Math.round(scores.agreeableness * 100)
    traits.push({
      id: 'agreeableness',
      left: t('gamer.collaborationProfile.personalityTraits.straightforward', { defaultValue: 'Straightforward' }),
      right: t('gamer.collaborationProfile.personalityTraits.empathic', { defaultValue: 'Empathic' }),
      value
    })
  }

  // Communication: Poor (left) vs Strong (right)
  if (scores.communication !== undefined) {
    const value = Math.round(scores.communication * 100)
    traits.push({
      id: 'communication',
      left: t('gamer.collaborationProfile.personalityTraits.poor', { defaultValue: 'Poor' }),
      right: t('gamer.collaborationProfile.personalityTraits.strong', { defaultValue: 'Strong' }),
      value
    })
  }

  // Helping/Assisting: Poor (left) vs Strong (right)
  if (scores.helpingAssisting !== undefined) {
    const value = Math.round(scores.helpingAssisting * 100)
    traits.push({
      id: 'helpingAssisting',
      left: t('gamer.collaborationProfile.personalityTraits.poor', { defaultValue: 'Poor' }),
      right: t('gamer.collaborationProfile.personalityTraits.strong', { defaultValue: 'Strong' }),
      value
    })
  }

  // Decision Making: Poor (left) vs Strong (right)
  if (scores.decisionMaking !== undefined) {
    const value = Math.round(scores.decisionMaking * 100)
    traits.push({
      id: 'decisionMaking',
      left: t('gamer.collaborationProfile.personalityTraits.poor', { defaultValue: 'Poor' }),
      right: t('gamer.collaborationProfile.personalityTraits.strong', { defaultValue: 'Strong' }),
      value
    })
  }

  return traits
}

/**
 * Get label for a variable from translation files
 *
 * Re-exports the shared getVariableLabel function for backward compatibility.
 * This wrapper maintains the old signature while using the new shared implementation.
 *
 * @param variable - The variable name
 * @param t - Translation function
 * @param context - Context ('strengths' | 'weaknesses')
 * @returns Translated label
 */
export function getVariableLabel(variable: string, t: TFunction, context: 'strengths' | 'weaknesses'): string {
  // Use shared utility with context option
  return getVariableLabelShared(variable, t, { context })
}

/**
 * Transform development plan data to MyProfileSubSectionCard format
 */
// A raw quote is the participant's own words, so it is shown as a quotation rather than as feedback about them.
const quoteAsFallback = (quote: string | undefined, t: TFunction): string =>
  quote ? t('gamer.collaborationProfile.inYourOwnWords', { quote, defaultValue: 'In your own words: “{{quote}}”' }) : ''

export function transformDevelopmentPlanData(
  reportData: UserProfileReportData,
  t: TFunction
): {
  whatIsWorking: MyProfileSectionData
  improvementAreas: MyProfileSectionData
  tryThisAtWork: MyProfileSectionData
} {
  const whatIsWorking: MyProfileSectionData = {}
  const improvementAreas: MyProfileSectionData = {}
  const tryThisAtWork: MyProfileSectionData = {}

  // Transform whatIsWorking
  const workingAreas = reportData.whatIsWorking?.areas ?? []
  if (workingAreas.length > 0) {
    workingAreas.forEach((area, index: number) => {
      const key = `section_${index}`
      const analysis = analysisToPlainText(area.llmAnalysis)
      whatIsWorking[key] = { text: analysis || quoteAsFallback(area.quote, t) }
    })
  }

  // Transform improvementAreas
  const improvementAreasList = reportData.improvementAreas?.areas ?? []
  if (improvementAreasList.length > 0) {
    improvementAreasList.forEach((area, index: number) => {
      const key = `section_${index}`
      const analysis = analysisToPlainText(area.llmAnalysis)
      improvementAreas[key] = { text: analysis || quoteAsFallback(area.quote, t) }
    })
  }

  // Transform tryThisAtWork - special handling: array becomes "Example 1", "Example 2", etc.
  if (reportData.tryThisAtWork && reportData.tryThisAtWork.length > 0) {
    reportData.tryThisAtWork.forEach((item: string, index: number) => {
      const key = `example_${index}`
      tryThisAtWork[key] = { text: item }
    })
  }

  return {
    whatIsWorking,
    improvementAreas,
    tryThisAtWork
  }
}

/** Their profile has strengths or watch-outs built from a game they have not read it since. */
export const hasUnreadProfileFeedback = (user: Pick<User, 'profileReport' | 'tasks'> | undefined): boolean => {
  const report = user?.profileReport
  const hasFeedbackBoxes = Boolean(report?.whatIsWorking?.areas?.length || report?.improvementAreas?.areas?.length)
  if (!hasFeedbackBoxes) return false

  const readSessionIds = new Set(user?.tasks?.profileFeedback?.sessionIds ?? [])
  return (report?.generatedFromSessionIds ?? []).some((sessionId) => !readSessionIds.has(sessionId))
}
